/**
 * CodeMirror StateEffects and StateFields for execution state.
 * These are pushed from React into CodeMirror via dispatch({ effects }).
 */

import { StateEffect, StateField } from "@codemirror/state";
import type { ParseDiagnostic } from "@magic-md/core";
import type { ForExecutionStatus } from "../../store/plugin-execution-store";
import type { RefResolveContext } from "./ref/resolution";
import type { DurationMap, ErrorMap, StatusMap } from "./types";

/* ── Execution status map pushed from React into CM ── */
export const setExecutionStatuses = StateEffect.define<StatusMap>();

export const executionStatusField = StateField.define<StatusMap>({
    create: () => new Map(),
    update(value, tr) {
        for (const e of tr.effects) {
            if (e.is(setExecutionStatuses)) return e.value;
        }
        return value;
    },
});

/* ── Error messages map pushed from React into CM ── */
export const setExecutionErrors = StateEffect.define<ErrorMap>();

export const executionErrorField = StateField.define<ErrorMap>({
    create: () => new Map(),
    update(value, tr) {
        for (const e of tr.effects) {
            if (e.is(setExecutionErrors)) return e.value;
        }
        return value;
    },
});

/* ── Duration map pushed from React into CM ── */
export const setExecutionDurations = StateEffect.define<DurationMap>();

export const executionDurationField = StateField.define<DurationMap>({
    create: () => new Map(),
    update(value, tr) {
        for (const e of tr.effects) {
            if (e.is(setExecutionDurations)) return e.value;
        }
        return value;
    },
});

/* ── For-execution status map pushed from React into CM ── */
export type ForStatusMap = Map<string, ForExecutionStatus>;
export const setForExecutionStatuses = StateEffect.define<ForStatusMap>();

export const forExecutionStatusField = StateField.define<ForStatusMap>({
    create: () => new Map(),
    update(value, tr) {
        for (const e of tr.effects) {
            if (e.is(setForExecutionStatuses)) return e.value;
        }
        return value;
    },
});

/* ── For-execution duration map pushed from React into CM ── */
export type ForDurationMap = Map<string, number | null>;
export const setForExecutionDurations = StateEffect.define<ForDurationMap>();

export const forExecutionDurationField = StateField.define<ForDurationMap>({
    create: () => new Map(),
    update(value, tr) {
        for (const e of tr.effects) {
            if (e.is(setForExecutionDurations)) return e.value;
        }
        return value;
    },
});

/* ── Parse diagnostics pushed from React into CM ── */
export const setParseDiagnostics = StateEffect.define<ParseDiagnostic[]>();

export const parseDiagnosticsField = StateField.define<ParseDiagnostic[]>({
    create: () => [],
    update(value, tr) {
        for (const e of tr.effects) {
            if (e.is(setParseDiagnostics)) return e.value;
        }
        return value;
    },
});

/* ── Pane-scoped $ref resolution context pushed from React into CM ── */
export const setRefResolveContext = StateEffect.define<RefResolveContext | null>();

export const refResolveContextField = StateField.define<RefResolveContext | null>({
    create: () => null,
    update(value, tr) {
        for (const e of tr.effects) {
            if (e.is(setRefResolveContext)) return e.value;
        }
        return value;
    },
});

/* ── Engine-resolved definitions map (defId → pluginName) pushed from React ── */
export type EngineDefMap = Map<string, string>;
export const setEngineDefinitions = StateEffect.define<EngineDefMap>();

export const engineDefinitionsField = StateField.define<EngineDefMap>({
    create: () => new Map(),
    update(value, tr) {
        for (const e of tr.effects) {
            if (e.is(setEngineDefinitions)) return e.value;
        }
        return value;
    },
});

/* ── Cloud Worker plugin names pushed from the UI registry into CM ── */
export type CloudWorkerPluginNames = Set<string>;
export const setCloudWorkerPluginNames =
    StateEffect.define<CloudWorkerPluginNames>();

export const cloudWorkerPluginNamesField = StateField.define<CloudWorkerPluginNames>({
    create: () => new Set(),
    update(value, tr) {
        for (const e of tr.effects) {
            if (e.is(setCloudWorkerPluginNames)) return e.value;
        }
        return value;
    },
});

/* ── Force L2 mode (disable pretty widgets, show raw syntax) ── */
export const setForceL2Mode = StateEffect.define<boolean>();

export const forceL2Field = StateField.define<boolean>({
    create: () => false,
    update(value, tr) {
        for (const e of tr.effects) {
            if (e.is(setForceL2Mode)) return e.value;
        }
        return value;
    },
});

/* ── Editor focus state mirrored into CM StateFields ── */
export const setMagicSyntaxFocused = StateEffect.define<boolean>();

export const magicSyntaxFocusedField = StateField.define<boolean>({
    create: () => false,
    update(value, tr) {
        for (const e of tr.effects) {
            if (e.is(setMagicSyntaxFocused)) return e.value;
        }
        return value;
    },
});

/* ── Inline render previews toggled from CM action buttons ── */
export type InlineRenderedDefinitions = Set<string>;
export const setInlineRenderedDefinitions = StateEffect.define<InlineRenderedDefinitions>();

export const inlineRenderedDefinitionsField = StateField.define<InlineRenderedDefinitions>({
    create: () => new Set(),
    update(value, tr) {
        for (const e of tr.effects) {
            if (e.is(setInlineRenderedDefinitions)) return e.value;
        }
        return value;
    },
});

/* ── Output watches mirrored into CM for L0 unit indicators ── */
export type WatchedUnitIds = Set<string>;
export const setWatchedUnitIds = StateEffect.define<WatchedUnitIds>();

export const watchedUnitIdsField = StateField.define<WatchedUnitIds>({
    create: () => new Set(),
    update(value, tr) {
        for (const e of tr.effects) {
            if (e.is(setWatchedUnitIds)) return e.value;
        }
        return value;
    },
});
